import { useTranslation } from 'react-i18next';
import { PhoneCall } from 'lucide-react';
import { useDialer } from '@/contexts/DialerContext';
import { useNow } from '@/hooks/useNow';
import { useLiveCallContact } from '@/hooks/useLiveCallContact';
import { formatCallDuration } from '@/components/dialer/FloatingCallBar';
import { RAIL_FOCUS } from './AppRail';

/**
 * DES-2 : sur le nouveau shell, l'appel en cours reste visible dans le rail,
 * quelle que soit la page. Un clic rouvre la fiche de la personne en ligne,
 * comme « Ouvrir la fiche » de la barre d'appel. Rien ne s'affiche hors appel,
 * ni après le raccroché (fiche à qualifier).
 * (L'appel entrant hors session rejoindra cette zone avec la page d'appel, W3.)
 */
export function LiveCallPill({ open }: { open: boolean }) {
  const { t } = useTranslation(['dialer']);
  const { connectedContact, isLive } = useLiveCallContact();
  const { setSelectedProspect } = useDialer() as { setSelectedProspect?: (p: Record<string, unknown>) => void };
  const now = useNow(1000, isLive);
  // Only a call in progress: an ended call waiting for its outcome is not one.
  if (!connectedContact || !isLive) return null;
  const cc = connectedContact;
  const name = String(cc.prospect_name || cc.name || t('dialer:livePill.unknown'));
  const duration = formatCallDuration(cc.callStartedAt ?? null, now);
  const label = t('dialer:livePill.label', { name, duration });
  const reopen = () => setSelectedProspect?.({
    id: cc.prospect_id || '',
    prospect_name: cc.prospect_name || cc.name || '',
    prospect_phone: cc.prospect_phone || cc.phone || '',
    prospect_company: cc.prospect_company || cc.company || '',
    prospect_title: cc.prospect_title || '',
    prospect_email: cc.prospect_email || cc.email || '',
    linkedin_url: cc.linkedin_url || '',
    website: cc.website || '',
  });
  return (
    <button
      type="button"
      onClick={reopen}
      data-testid="live-call-pill"
      aria-label={label}
      title={label}
      className={`flex min-h-[40px] items-center rounded-lg text-[12px] ${RAIL_FOCUS}`}
      style={{
        gap: 8,
        padding: open ? '0 10px' : 0,
        justifyContent: open ? 'flex-start' : 'center',
        color: 'var(--vf-status-connected-fg)',
        background: 'color-mix(in srgb, var(--vf-status-connected-fg) 8%, var(--vf-bg))',
        border: '1px solid color-mix(in srgb, var(--vf-status-connected-fg) 20%, transparent)',
      }}
    >
      <PhoneCall size={14} aria-hidden />
      {open && (
        <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
          <span className="text-[11px] font-semibold">{t('dialer:livePill.title')}</span>
          <span className="truncate" style={{ color: 'var(--vf-text-1)' }}>{name}</span>
        </span>
      )}
      {open && <span className="vf-font-mono tabular-nums text-[12px] font-semibold">{duration}</span>}
    </button>
  );
}

export default LiveCallPill;
